import React from "react";
import { ExternalLink, Check, Layers, Scissors, Flag, Sparkles } from "lucide-react";
import { useQuote } from "../QuoteDialog";

const POINTS = [
  { icon: Layers, text: "Laminators (hot & cold)" },
  { icon: Scissors, text: "Vinyl cutters for decals & lettering" },
  { icon: Flag, text: "Banner accessories & finishing kits" },
  { icon: Sparkles, text: "Polished, durable, display-ready output" },
];

const HERO_IMG =
  "https://images.unsplash.com/photo-1672356203083-a0206308c7ea?fm=webp&q=85&w=2000&fit=crop";

export default function FinishingHero() {
  const { openQuote } = useQuote();
  return (
    <section
      data-testid="finishing-hero-section"
      className="relative isolate overflow-hidden bg-navy"
    >
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Color print being finished — laminating, cutting, and producing display-ready materials"
          className="w-full h-full object-cover opacity-50"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F1F4D] via-[#0F1F4D]/85 to-[#0F1F4D]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1F4D]/80 via-transparent to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-20 pb-24 md:pt-28 md:pb-32 lg:pt-36 lg:pb-40">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white text-xs font-bold uppercase tracking-[0.18em] mb-8 animate-fade-up">
            <span className="w-1.5 h-1.5 rounded-full bg-teal" />
            Laminators · Vinyl Cutters · Banner Accessories
          </div>

          <h1
            data-testid="finishing-hero-headline"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tight text-white leading-[1.05] animate-fade-up"
            style={{ animationDelay: "80ms" }}
          >
            Don't Just Print.{" "}
            <span className="text-teal">Finish Like a Pro.</span>
          </h1>

          <p
            className="mt-8 text-lg sm:text-xl text-slate-200 leading-relaxed max-w-2xl animate-fade-up"
            style={{ animationDelay: "160ms" }}
          >
            Give your school the tools to laminate, cut, and finish posters and
            banners in-house—so everything looks polished, durable, and
            display-ready.
          </p>

          <div
            className="mt-10 animate-fade-up"
            style={{ animationDelay: "240ms" }}
          >
            <button
              data-testid="finishing-hero-quote-btn"
              onClick={openQuote}
            title="Opens in a new tab"
              className="group inline-flex items-center justify-center gap-2 bg-teal hover:bg-[#0D6BB1] text-white font-bold px-8 py-4 rounded-lg shadow-xl hover:shadow-2xl transition-all duration-200 hover:-translate-y-0.5"
            >
              Request a Quote
              <ExternalLink className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>

          <ul
            className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 max-w-2xl animate-fade-up"
            style={{ animationDelay: "320ms" }}
          >
            {POINTS.map((p) => {
              const Icon = p.icon;
              return (
                <li key={p.text} className="flex items-start gap-3 text-slate-200">
                  <span className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full bg-teal/20 border border-teal/40 flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-teal" strokeWidth={3} />
                  </span>
                  <span className="text-sm font-medium flex items-center gap-2">
                    <Icon className="w-4 h-4 text-teal" strokeWidth={2.5} />
                    {p.text}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
